<template>
  <Band>
    <div class="md:flex md:items-end md:justify-between md:gap-12">
      <div>
        <Heading
          type="h1"
          variant="h2"
          :flourish="$t('cobrand_and_share')"
          flourish-color="brand"
          :ui="{ base: 'mt-1', flourish: 'uppercase' }"
        >
          {{ $t(headingKey) }}
        </Heading>

        <Markdown
          as="p"
          class="mt-4 max-w-xl text-base"
          :text="$t('content_experiences_description')"
        />
        <MarketPreferredSuppliers class="mt-6" />
      </div>

      <ContentExperienceSearchInput
        v-if="!search.hasTags.value"
        v-model="search.searchTerm.value"
        :placeholder="searchPlaceholder"
        class="mt-6 shrink-0 md:mt-0"
      />
    </div>

    <div class="mt-8">
      <div
        v-if="partner.content_experiences_count"
        class="border-b"
      >
        <nav class="-mb-px flex space-x-8">
          <NuxtLink
            :to="`/${partner.slug}/experiences`"
            class="experiences-tab"
            exact-active-class="experiences-tab--active"
          >
            {{ $t('all_experiences') }}
          </NuxtLink>

          <NuxtLink
            v-if="isLoggedIn"
            :to="`/${partner.slug}/experiences/active`"
            class="experiences-tab"
            exact-active-class="experiences-tab--active"
          >
            {{ $t('your_experiences') }}
          </NuxtLink>

          <NuxtLink
            :to="`/${partner.slug}/experiences/new`"
            class="experiences-tab"
            exact-active-class="experiences-tab--active"
          >
            {{ $t('new_experiences') }}
            <span
              v-if="partner.new_content_experiences_count"
              class="ml-3 hidden rounded-full bg-pink-500 px-2.5 py-0.5 text-xs font-semibold text-white md:inline-flex md:items-center"
            >{{ partner.new_content_experiences_count }}</span>
          </NuxtLink>
        </nav>
      </div>

      <NuxtPage
        :class="{ 'mt-8': partner.content_experiences_count }"
        :partner="partner"
      />
    </div>
  </Band>
</template>

<script setup lang="ts">
import type { Partner } from '~/types/partner'

definePageMeta({
  source: 'brand-experiences'
})

const { isLoggedIn } = useAuth()
const { t } = useI18n()
const route = useRoute()
const partner = useAttrs().partner as Partner

const search = provideExperiencesSearch()
watch(() => route.path, () => (search.searchTerm.value = ''))

const searchPlaceholder = computed(() => {
  if (route.path.endsWith('/experiences/active')) return t('search_your_experiences', 'Search your experiences')
  if (route.path.endsWith('/experiences/new')) return t('search_new_experiences', 'Search new experiences')

  return t('search_all_experiences', 'Search all experiences')
})

// Title mirrors the main experiences page; tracks the active tab and falls back to the prompt when the user has no experiences with this partner.
const headingKey = computed(() => {
  if (!partner.content_experiences_count) return 'choose_a_content_experience'

  if (route.path.endsWith('/experiences/active')) return 'your_content_experiences'
  if (route.path.endsWith('/experiences/new')) return 'new_content_experiences'

  return 'all_content_experiences'
})

// Head
const { fbAppId, frontendUrl, siteName } = useRuntimeConfig().public

const head = {
  title: `Your ${partner.name} Content Experiences`,
  description: `Your ${partner.name} co-branded content experiences. Powered by ${siteName}.`
}

useHead({
  title: head.title,
  meta: [
    // General
    { name: 'description', content: head.description },
    // OpenGraph
    { property: 'fb:app_id', content: fbAppId },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: frontendUrl + route.fullPath },
    { property: 'og:title', content: head.title },
    { property: 'og:description', content: head.description },
    { property: 'og:image', content: '' },
    { property: 'og:locale', content: 'en' },
    { property: 'og:site_name', content: siteName },
    // Twitter Card
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: head.title },
    { name: 'twitter:description', content: head.description },
    { name: 'twitter:image', content: '' }
  ]
})
</script>

<style scoped>
.experiences-tab {
  @apply flex items-center whitespace-nowrap border-b-2 border-transparent pb-4 text-sm font-medium text-gray-500 hover:text-brand;
}

.experiences-tab--active {
  @apply border-brand text-brand hover:cursor-default hover:text-brand;
}
</style>
